import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AuthShell } from "../components/AuthShell";
import { SeoForPage } from "../components/Seo";
import { useT, useLp } from "../lib/i18n";
import { getEntitlement, downloadUrl, type Entitlement } from "../lib/api";
import { refreshSession, useSession } from "../lib/auth";
import { M1_PLAUSIBLE_EVENTS, trackPlausibleEvent } from "../lib/plausible";
import { attributionProps } from "../lib/attribution";
import { trackLaunchPaidOnce } from "../lib/launch-funnel";

/**
 * Post-checkout landing page. Stripe redirects here with
 * `?session_id={CHECKOUT_SESSION_ID}` after a successful Checkout. We don't
 * read the session_id — the Go webhook has already written to DynamoDB and
 * flipped the Cognito group by the time the user lands. What we DO care about:
 *
 *   1. Refresh the site's JWT once so the new `pro_users` group shows up in
 *      claims for subsequent same-tab API calls.
 *   2. Poll `/users/me/entitlement` until it reports `plan=pro` or we hit a
 *      small backoff ceiling — webhook delivery is usually <2s but we've
 *      seen up to ~15s under load.
 *
 * No timer tricks: if the webhook gets stuck, we stop the loop and show a
 * "refresh status" button so the user can retry manually instead of sitting
 * on an indefinite spinner.
 */
const POLL_INTERVAL_MS = 2000;
const POLL_ATTEMPTS = 8; // ~16s total budget
const TRACK_ONCE_PREFIX = "plausible.once";

function trackOncePerCheckout(
  event: string,
  checkoutSessionKey: string,
): void {
  const storageKey = `${TRACK_ONCE_PREFIX}.${event}.${checkoutSessionKey}`;
  try {
    if (sessionStorage.getItem(storageKey)) return;
    sessionStorage.setItem(storageKey, "1");
  } catch {
    // Ignore storage failures (privacy mode / blocked storage) and still emit.
  }
  trackPlausibleEvent(event, attributionProps());
}

export function BillingDone() {
  const t = useT();
  const lp = useLp();
  const td = t.billing.done;
  const [params] = useSearchParams();
  const session = useSession();
  const checkoutSessionId = params.get("session_id");
  const checkoutStatus = params.get("status");
  const hasStripeSuccessParam =
    Boolean(checkoutSessionId) ||
    checkoutStatus === "succeeded" ||
    checkoutStatus === "success";
  const checkoutSessionKey = checkoutSessionId ?? checkoutStatus ?? "unknown";

  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [errored, setErrored] = useState(false);
  const didRefreshRef = useRef(false);

  // Single-shot token refresh so the pro_users group lands in the next
  // id_token. Keep the rest of the polling loop running even if this fails
  // (user may have logged out, tokens may be expired — worst case we still
  // read entitlement straight from the API with the old access token).
  useEffect(() => {
    if (didRefreshRef.current) return;
    didRefreshRef.current = true;
    refreshSession().catch(() => {});
  }, []);

  useEffect(() => {
    if (!session) return;
    if (entitlement?.plan === "pro") return;
    if (attempts >= POLL_ATTEMPTS) return;

    let cancelled = false;
    const id = setTimeout(() => {
      getEntitlement(session.tokens.access_token)
        .then((e) => {
          if (cancelled) return;
          setEntitlement(e);
          if (e.plan !== "pro") setAttempts((n) => n + 1);
        })
        .catch(() => {
          if (cancelled) return;
          setErrored(true);
          setAttempts((n) => n + 1);
        });
    }, attempts === 0 ? 0 : POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [session, entitlement?.plan, attempts]);

  const handleRefresh = () => {
    setErrored(false);
    setAttempts(0);
  };

  const ready = entitlement?.plan === "pro";
  const pending = !ready && !errored && attempts < POLL_ATTEMPTS;

  useEffect(() => {
    if (!hasStripeSuccessParam) return;
    trackOncePerCheckout(M1_PLAUSIBLE_EVENTS.signupPro, checkoutSessionKey);
  }, [hasStripeSuccessParam, checkoutSessionKey]);

  useEffect(() => {
    if (!ready || !hasStripeSuccessParam) return;
    trackOncePerCheckout(M1_PLAUSIBLE_EVENTS.checkoutCompleted, checkoutSessionKey);
    trackLaunchPaidOnce(checkoutSessionKey);
  }, [ready, hasStripeSuccessParam, checkoutSessionKey]);

  return (
    <AuthShell>
      <SeoForPage page="billingDone" noindex />
      <div className="text-center">
        <div
          className={[
            "inline-flex h-12 w-12 items-center justify-center rounded-full",
            ready
              ? "bg-done/15 text-done"
              : "bg-margin text-ink/50 dark:bg-paper/10 dark:text-paper/50",
          ].join(" ")}
        >
          {ready ? (
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
              <polyline points="4,10 8,14 16,6" />
            </svg>
          ) : (
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`h-6 w-6 ${pending ? "animate-spin" : ""}`}>
              <path d="M10 2v4M10 14v4M4 10H2M18 10h-2M5.6 5.6l1.4 1.4M13 13l1.4 1.4M5.6 14.4l1.4-1.4M13 7l1.4-1.4" />
            </svg>
          )}
        </div>

        <h1 className="mt-6 font-display text-3xl font-semibold tracking-tight text-ink dark:text-paper">
          {td.heading}
        </h1>
        <p className="mt-3 text-sm text-ink/60 dark:text-paper/60">
          {ready ? td.subReady : td.subProcessing}
        </p>

        {errored && !ready && (
          <p className="mt-4 text-sm text-rec">{td.error}</p>
        )}

        <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
          {ready ? (
            <a
              href={downloadUrl()}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-graphite text-paper hover:bg-graphite-muted transition-colors px-6 py-3 text-sm font-medium"
            >
              {td.openApp}
            </a>
          ) : (
            <button
              type="button"
              onClick={handleRefresh}
              disabled={pending}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-graphite text-paper hover:bg-graphite-muted disabled:opacity-60 disabled:cursor-not-allowed transition-colors px-6 py-3 text-sm font-medium"
            >
              {td.refreshCta}
            </button>
          )}
          <Link
            to={lp + "/"}
            className="inline-flex items-center justify-center rounded-full border border-ink/20 dark:border-paper/30 px-6 py-3 text-sm font-medium text-ink dark:text-paper hover:bg-ink/5 dark:hover:bg-paper/5 transition-colors"
          >
            {td.homeCta}
          </Link>
        </div>
      </div>
    </AuthShell>
  );
}
